import { Suspense, lazy, useEffect, useState } from 'react';
import { dayNumber } from './lib/game';
import { MODES, LANGS, ENABLED_MODES, pathFor, type Lang } from './lib/routes';
import { modeStatus } from './lib/storage';
import { isGlobalKilled, isModeKilled } from './lib/killswitch';
import { useApp } from './app-context';
import Game from './components/Game';
import SeoContent from './components/SeoContent';

// Code-split: characters.json (~397 entries) + char-mode code load only on demand,
// keeping them out of the initial bundle (PageSpeed: reduce unused JS).
const CharacterMode = lazy(() => import('./components/CharacterMode'));

const DMCA_EMAIL = 'dmca@animedle.today';

function useModeStatuses(): Record<string, string> {
  const read = () => Object.fromEntries(ENABLED_MODES.map((m) => [m, modeStatus(m)]));
  const [statuses, setStatuses] = useState<Record<string, string>>(read);
  useEffect(() => {
    const on = () => setStatuses(read());
    window.addEventListener('animedle:progress', on);
    return () => window.removeEventListener('animedle:progress', on);
  }, []);
  return statuses;
}

export default function App() {
  const { lang, mode, navigate, setLang, t } = useApp();
  const statuses = useModeStatuses();
  const doneCount = ENABLED_MODES.filter((m) => statuses[m] === 'won').length;

  // Re-render once the live takedown config loads (after first paint) so a global
  // or per-mode kill applies even though it isn't awaited before render.
  const [, bumpKs] = useState(0);
  useEffect(() => {
    const on = () => bumpKs((v) => v + 1);
    window.addEventListener('animedle:killswitch', on);
    return () => window.removeEventListener('animedle:killswitch', on);
  }, []);

  // Global takedown kill-switch (best-practice §4.4): show a notice, hide the game.
  if (isGlobalKilled()) {
    return (
      <div className="sheet">
        <header className="masthead">
          <span className="logo">ANIMEDL<span className="y">E</span></span>
        </header>
        <section className="takedown">
          <h1>{t('takedown.title')}</h1>
          <p>{t('takedown.body')}</p>
          <p className="dmca">{t('dmca.line')} <a href={`mailto:${DMCA_EMAIL}`}>{DMCA_EMAIL}</a></p>
        </section>
      </div>
    );
  }

  const modeKilled = isModeKilled(mode);
  const heroTitle = t(`hero.title.${mode}`) === `hero.title.${mode}` ? t('hero.title') : t(`hero.title.${mode}`);
  const heroSub = t(`hero.sub.${mode}`) === `hero.sub.${mode}` ? t('hero.sub') : t(`hero.sub.${mode}`);

  return (
    <div className="sheet">
      <header className="masthead">
        <a className="logo" href={pathFor(lang, 'classic')} onClick={(e) => { e.preventDefault(); navigate(lang, 'classic'); }}>
          ANIMEDL<span className="y">E</span>
        </a>
        <div className="issue">
          <div className="ji">#{dayNumber()}</div>
          <div className="sub">{t('sub.daily')}</div>
        </div>
        <div className="langsw" role="navigation" aria-label="Language">
          {LANGS.map((l) => (
            <a key={l} href={pathFor(l, mode)} className={l === lang ? 'on' : ''}
              onClick={(e) => { e.preventDefault(); setLang(l as Lang); }}>{l.toUpperCase()}</a>
          ))}
        </div>
      </header>

      <main>
        <section className="hero">
          <span className="onoma">ドン!!</span>
          <span className="kicker">{t('kicker')}</span>
          <h1>{heroTitle}</h1>
          <p>{heroSub}</p>
        </section>

        <nav className="modes" aria-label="Game modes">
          {MODES.map((m) => {
            const active = m.key === mode;
            const killed = isModeKilled(m.key);
            const soon = !m.enabled || killed;
            const won = statuses[m.key] === 'won';
            const inner = (
              <>
                <span className="ic">{won && !active ? <span className="check">✓</span> : m.icon}</span>
                <span className="lb">{t(`mode.${m.key}`)}</span>
              </>
            );
            // Enabled modes are real, crawlable links; disabled/coming-soon are
            // buttons (not links) so they aren't flagged as uncrawlable.
            return soon ? (
              <button key={m.key} type="button" disabled className="mode soon"
                title={killed ? t('mode.unavailable') : t('mode.soon')}>{inner}</button>
            ) : (
              <a key={m.key} className={`mode ${active ? 'active' : ''} ${won && !active ? 'done' : ''}`}
                href={pathFor(lang, m.key)} title={t(`mode.${m.key}`)}
                onClick={(e) => { e.preventDefault(); navigate(lang, m.key); }}>{inner}</a>
            );
          })}
          <div className="today-progress" title={t('today.progress', { n: doneCount, m: ENABLED_MODES.length })}>
            <span className="tp-label">{t('today.label')}</span>
            <span className="tp-count">{doneCount}/{ENABLED_MODES.length}</span>
          </div>
        </nav>

        {modeKilled ? (
          <section className="panel mode-unavailable">
            <p>{t('mode.unavailable.body')}</p>
            <button className="ku-btn" onClick={() => navigate(lang, 'classic')}>→ {t('mode.classic')}</button>
          </section>
        ) : mode === 'character' ? (
          <Suspense fallback={<section className="panel"><div className="empty">…</div></section>}>
            <CharacterMode key={mode} mode={mode} />
          </Suspense>
        ) : (
          <Game key={mode} mode={mode} />
        )}

        <SeoContent lang={lang} />
      </main>

      <footer className="disclaimer">
        {t('disclaimer.1')}<br />
        {t('disclaimer.2')}{' '}
        <a href="https://anilist.co" target="_blank" rel="noopener noreferrer">AniList</a>.<br />
        {t('dmca.line')} <a href={`mailto:${DMCA_EMAIL}`}>{DMCA_EMAIL}</a>
      </footer>
    </div>
  );
}
